// packages/web-ui/src/api/internal/api-call.ts
//
// AC #6: typed `apiCall<TReq, TRes>` helper.
// Returns `Result<T, ApiError>` per Arch §8.6 + parent CLAUDE.md
// "All public functions return `Result<T, E>` — no throws across package
// boundaries". The web-ui carve-out (Arch §2.6) allows internal React helpers
// to throw — but the API client may NOT.

import type { Result } from '@tessa-lean/base';

export interface ApiError {
  code: string;
  message: string;
  retryable: boolean;
  detail?: unknown;
  requestId: string;
}

export interface ApiContext {
  /** '' in dev (proxy), '' in thin-client embed (same origin), absolute URL in standalone tests. */
  baseUrl: string;
  /** Bootstrap token sourced from `localStorage.tessaLeanToken`. `null` for unauthenticated dev. */
  token: string | null;
  /** Override fetch implementation (used in unit tests). */
  fetchImpl?: typeof fetch;
}

/**
 * Storage key used by the SPA to persist the bootstrap bearer token. Kept in
 * one place so the helper below + `<SseProvider>` + (future) auth gate all
 * agree on the same key (W2-MED-4 polish — closes the divergent-defaults trap
 * tracked in `_bmad-output/implementation-artifacts/post-merge-polish-followups.md`).
 */
export const TESSA_LEAN_TOKEN_STORAGE_KEY = 'tessaLeanToken';

/**
 * Resolve the default {@link ApiContext} for SPA call-sites. Reads the bearer
 * token from `localStorage[TESSA_LEAN_TOKEN_STORAGE_KEY]` when available and
 * defaults to a same-origin `baseUrl: ''` so dev (Vite proxy), thin-client
 * embed (extension webview), and standalone deployments all work without
 * extra configuration.
 *
 * Centralised in MED-4 — prior callers built their own `defaultApiContext`
 * helper inline (or used the literal `{ baseUrl: '', token: null }`), which
 * would diverge once bearer-token auth lands. All in-scope call-sites now
 * delegate here.
 */
export function getDefaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(TESSA_LEAN_TOKEN_STORAGE_KEY)
      : null;
  return { baseUrl: '', token };
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Best-effort parse of the server's `ApiError` envelope (Arch §3.4). When the
 * response body is not valid JSON or does not match the expected shape we
 * synthesize a fallback envelope so callers always get a stable structure.
 */
async function parseApiErrorEnvelope(response: Response, requestId: string): Promise<ApiError> {
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    return {
      code: 'HTTP_ERROR',
      message: `${response.status} ${response.statusText}`,
      retryable: response.status >= 500,
      requestId,
    };
  }
  if (raw !== null && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const code = typeof obj.code === 'string' ? obj.code : 'HTTP_ERROR';
    const message = typeof obj.message === 'string' ? obj.message : `${response.status} ${response.statusText}`;
    const retryable = typeof obj.retryable === 'boolean' ? obj.retryable : response.status >= 500;
    const envelopeRequestId = typeof obj.requestId === 'string' ? obj.requestId : requestId;
    return {
      code,
      message,
      retryable,
      detail: obj.detail,
      requestId: envelopeRequestId,
    };
  }
  return {
    code: 'HTTP_ERROR',
    message: `${response.status} ${response.statusText}`,
    retryable: response.status >= 500,
    requestId,
  };
}

export interface BinaryCallOptions {
  /** MIME type to request via `Accept`. Default `application/octet-stream`. */
  accept?: string;
  /** Filename to use when the server omits `Content-Disposition`. */
  filenameHint?: string;
}

export interface BinaryResult {
  bytes: Blob;
  filename: string;
}

/**
 * Best-effort `Content-Disposition: attachment; filename="…"` parser.
 * Returns `null` when the header is absent or unparseable.
 */
function parseDispositionFilename(header: string | null): string | null {
  if (header === null || header.length === 0) return null;
  // RFC 5987 `filename*=UTF-8''<encoded>` takes precedence.
  const star = /filename\*\s*=\s*[^']*''([^;\r\n]+)/i.exec(header);
  if (star !== null && typeof star[1] === 'string') {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*"?([^";\r\n]+)"?/i.exec(header);
  if (plain !== null && typeof plain[1] === 'string') return plain[1];
  return null;
}

/**
 * Variant of {@link apiCall} for binary payloads (e.g. SVG exports). Reads
 * the response body as a `Blob` and parses `Content-Disposition` for a
 * download filename. Failures are surfaced via the same `ApiError` envelope
 * — no exceptions cross the package boundary (CLAUDE.md "Coding Standards").
 */
export async function apiCallBinary(
  ctx: ApiContext,
  method: HttpMethod,
  path: string,
  options: BinaryCallOptions = {},
): Promise<Result<BinaryResult, ApiError>> {
  const requestId = crypto.randomUUID();
  const headers: Record<string, string> = {
    Accept: options.accept ?? 'application/octet-stream',
    'X-Request-Id': requestId,
  };
  if (ctx.token !== null) headers.Authorization = `Bearer ${ctx.token}`;

  const fetchImpl = ctx.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await fetchImpl(`${ctx.baseUrl}${path}`, {
      method,
      headers,
      // Send cookies for the multi-user auth session (tessa_lean_auth cookie).
      credentials: 'include',
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: err instanceof Error ? err.message : 'fetch failed',
        retryable: true,
        requestId,
      },
    };
  }

  if (!response.ok) {
    const envelope = await parseApiErrorEnvelope(response, requestId);
    return { ok: false, error: envelope };
  }

  let bytes: Blob;
  try {
    bytes = await response.blob();
  } catch (err) {
    return {
      ok: false,
      error: {
        code: 'RESPONSE_PARSE_ERROR',
        message: err instanceof Error ? err.message : 'failed to read response body',
        retryable: false,
        requestId,
      },
    };
  }

  const filename =
    parseDispositionFilename(response.headers.get('content-disposition')) ??
    options.filenameHint ??
    'download';

  return { ok: true, value: { bytes, filename } };
}

export async function apiCall<TReq, TRes>(
  ctx: ApiContext,
  method: HttpMethod,
  path: string,
  body?: TReq,
): Promise<Result<TRes, ApiError>> {
  const requestId = crypto.randomUUID();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (ctx.token !== null) headers.Authorization = `Bearer ${ctx.token}`;

  const fetchImpl = ctx.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await fetchImpl(`${ctx.baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      // Send cookies for the multi-user auth session (tessa_lean_auth cookie).
      credentials: 'include',
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: err instanceof Error ? err.message : 'fetch failed',
        retryable: true,
        requestId,
      },
    };
  }

  if (!response.ok) {
    const envelope = await parseApiErrorEnvelope(response, requestId);
    return { ok: false, error: envelope };
  }

  // 2xx — parse body. 204 = empty.
  if (response.status === 204) return { ok: true, value: undefined as TRes };
  try {
    const data = (await response.json()) as TRes;
    return { ok: true, value: data };
  } catch (err) {
    return {
      ok: false,
      error: {
        code: 'RESPONSE_PARSE_ERROR',
        message: err instanceof Error ? err.message : 'response body was not valid JSON',
        retryable: false,
        requestId,
      },
    };
  }
}
